import React from "react";
import { Button, ErrorAlert, Field } from "src/components";
import FileField from "src/components/UI/file-field";
import IconTooltip, { IconTooltipType } from "src/components/iconTooltip";
import { KycFallback } from "src/api";
import { useAlfredKycFallback } from "src/hooks";
import { _t } from "src/utils";
import AlfredKycFallbackReview from "./AlfredKycFallbackReview";
import { getKycFieldMeta } from "./kyc-field-meta";
import "./alfred-kyc-fallback.scss";

interface Props {
  fallback: KycFallback;
}

/** Humanize Alfred's camelCase/snake_case fieldNames for labels. */
const humanizeFieldName = (fieldName: string): string =>
  fieldName
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());

const AlfredKycFallbackForm: React.FC<Props> = ({ fallback }) => {
  const {
    corrections,
    documents,
    touched,
    fieldErrors,
    submitting,
    submitted,
    error,
    canSubmit,
    setCorrection,
    setDocument,
    markTouched,
    handleSubmit,
  } = useAlfredKycFallback({ fallback });

  if (submitted) return <AlfredKycFallbackReview />;

  return (
    <div className="alfred-blocker alfred-kyc-fallback" role="group" aria-label="KYC update required">
      <h2 className="alfred-blocker__title">
        {_t("ALFRED.KYC_FALLBACK.TITLE", { defaultValue: "Additional details needed" })}
      </h2>
      <p className="alfred-blocker__message">
        {_t("ALFRED.KYC_FALLBACK.SUBTITLE", {
          defaultValue:
            "The payment provider needs you to update the following details before you can continue.",
        })}
      </p>

      <div className="alfred-kyc-fallback__fields">
        {fallback.fields
          // FILE_UPLOAD fields are re-rendered from `documents` below, so
          // skip them here to avoid a duplicate (text) input.
          .filter((field) => field.type !== "FILE_UPLOAD")
          .map((field) => {
            const meta = getKycFieldMeta(field.fieldName, fallback.country);
            const labelText =
              (meta.label ?? humanizeFieldName(field.fieldName)) + (field.required ? " *" : "");
            const tipId = `kyc-tip-${field.fieldName}`;
            const labelNode = (
              <span className="alfred-kyc-fallback__label">
                {labelText}
                {meta.tooltip && (
                  <IconTooltip
                    iconType={IconTooltipType.INFO}
                    className="alfred-kyc-fallback__tip"
                    tooltipId={tipId}
                    tooltipContent={meta.tooltip}
                  />
                )}
              </span>
            );
            const err = touched[field.fieldName] ? fieldErrors[field.fieldName] : undefined;

            if (field.type === "BOOLEAN") {
              const id = `kyc-cb-${field.fieldName}`;
              const checked = corrections[field.fieldName] === "true";
              return (
                <div key={field.fieldName} className="alfred-kyc-fallback__checkbox">
                  <input
                    id={id}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      setCorrection(field.fieldName, String(e.target.checked));
                      markTouched(field.fieldName);
                    }}
                    disabled={submitting}
                  />
                  <label htmlFor={id}>{labelText}</label>
                  {meta.tooltip && (
                    <IconTooltip
                      iconType={IconTooltipType.INFO}
                      className="alfred-kyc-fallback__tip"
                      tooltipId={tipId}
                      tooltipContent={meta.tooltip}
                    />
                  )}
                </div>
              );
            }

            if (field.type === "DATE") {
              return (
                <div key={field.fieldName} className="alfred-kyc-fallback__field">
                  <label className="field-label alfred-kyc-fallback__date-label">{labelNode}</label>
                  <input
                    type="date"
                    className="field-input"
                    value={corrections[field.fieldName] ?? ""}
                    onChange={(e) => setCorrection(field.fieldName, e.target.value)}
                    onBlur={() => markTouched(field.fieldName)}
                    disabled={submitting}
                  />
                  {err && <small className="alfred-kyc-fallback__error">{err}</small>}
                </div>
              );
            }

            // STRING (default). `country` is fixed by the user's verified KYC —
            // shown read-only, never overridable here.
            const isCountry = field.fieldName === "country";
            return (
              <div key={field.fieldName} className="alfred-kyc-fallback__field">
                <Field
                  label={labelNode}
                  placeholder={meta.placeholder}
                  value={corrections[field.fieldName] ?? ""}
                  onChange={(v) =>
                    setCorrection(field.fieldName, meta.sanitize ? meta.sanitize(v) : v)
                  }
                  onBlur={() => markTouched(field.fieldName)}
                  disabled={submitting || isCountry}
                  error={err}
                  maxLength={meta.maxLength}
                />
              </div>
            );
          })}

        {fallback.documents.map((doc) => {
          const label =
            _t("ALFRED.KYC_FALLBACK.DOCUMENT_LABEL", {
              defaultValue: "Upload {{document}}",
              document: humanizeFieldName(doc.fieldName),
            }) + " *";
          const err = touched[doc.fieldName] ? fieldErrors[doc.fieldName] : undefined;
          return (
            <div key={doc.fieldName} className="alfred-kyc-fallback__field">
              <FileField
                label={label}
                value={documents[doc.fieldName] ?? null}
                error={err}
                disabled={submitting}
                onChange={(file) => {
                  setDocument(doc.fieldName, file);
                  markTouched(doc.fieldName);
                }}
              />
            </div>
          );
        })}
      </div>

      <ErrorAlert message={error} />

      <Button
        title={
          submitting
            ? _t("ALFRED.KYC_FALLBACK.SUBMITTING", { defaultValue: "Submitting…" })
            : _t("ALFRED.KYC_FALLBACK.SUBMIT", { defaultValue: "Submit for review" })
        }
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        pending={submitting}
        className="alfred-kyc-fallback__submit"
      />
    </div>
  );
};

export default AlfredKycFallbackForm;
